import { useEffect, useState } from 'react'

import { useCreatePacsSources } from '../hooks/useCreatePacsSources'
import { useLightMode } from '@/hooks/useLightMode'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Flex, Modal, Text } from '@hivewatch/dark'

import { ControlledSelect, ControlledTextInput } from '@/components/ui'
import { removeUnderscoreAndCapitalize } from '@/utils/formats'
import { tVar } from '@/utils/i18n'

import { FormInputsT } from '../types/types'
import { ACS_KEY, INSTANCE_NAME_KEY } from '../utils/utils'

interface DarkCreatePACSModalIProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Rebrand variant of {@link CreatePACSCredentialsModal} built on
 * `@hivewatch/dark`. The ACS selection still drives which credential fields
 * render, and the source's `secure` fields still mask their input.
 */
export const DarkCreatePACSCredentialsModal = ({
  isOpen,
  onClose,
}: DarkCreatePACSModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { handleSubmit, control, watch, getValues, formState, reset, errors } =
    useForm({
      mode: 'onChange',
      reValidateMode: 'onChange',
    })
  const [isDirty, setIsDirty] = useState(false)
  const accessControlOption = watch(ACS_KEY)
  const { isLoading, pacsOptions, pacsSource, create } = useCreatePacsSources({
    pacSourceId: getValues(ACS_KEY)?.value,
    onClose,
  })

  const onSubmit: SubmitHandler<FormInputsT> = async (values) => {
    create(values)
  }

  useEffect(() => {
    reset({ acs: getValues(ACS_KEY) })
    setIsDirty(false)
  }, [accessControlOption])

  const subtitleColor =
    colorScheme === 'light' ? 'light.text.secondary' : 'text.secondary'

  return (
    <Modal
      bodyStyleOverrides={{ minH: 0, overflowY: 'auto' }}
      colorScheme={colorScheme}
      header={
        <Text color={subtitleColor} textStyle='body'>
          {t('settings:pacs_add_credentials_subtitle')}
        </Text>
      }
      isOpen={isOpen}
      isSaveDisabled={!formState.isValid || !isDirty}
      maxW='600px'
      onClose={onClose}
      onSave={handleSubmit(onSubmit)}
      saveButtonLabel={t('common:save')}
      showCancelButton
      size='compact'
      title={t('settings:pacs_add_credentials_title')}
    >
      <Flex direction='column' gap='16px'>
        <ControlledSelect
          colorScheme={colorScheme}
          control={control}
          isDisabled={isLoading}
          label={t('settings:pacs_acs_label')}
          name={ACS_KEY}
          options={pacsOptions}
          placeholder={t('settings:pacs_acs_placeholder')}
          rules={{ required: true }}
        />

        {pacsSource && (
          <ControlledTextInput
            colorScheme={colorScheme}
            control={control}
            errors={errors}
            label={t('common:name')}
            name={INSTANCE_NAME_KEY}
            placeholder={t('common:enter_name')}
            rules={{ required: t('settings:pacs_name_required') }}
            width='100%'
          />
        )}

        {pacsSource?.fields?.map((p) => {
          const name = removeUnderscoreAndCapitalize(p.name)
          return (
            <ControlledTextInput
              colorScheme={colorScheme}
              control={control}
              errors={errors}
              inputProps={p.secure ? { type: 'password' } : undefined}
              key={p.id}
              label={`${name}*`}
              name={p.id}
              onValueChange={() => setIsDirty(true)}
              placeholder={tVar(t, 'settings:pacs_field_placeholder', {
                field: name,
              })}
              rules={{
                required: tVar(t, 'settings:pacs_field_required', {
                  field: name,
                }),
              }}
              width='100%'
            />
          )
        })}
      </Flex>
    </Modal>
  )
}
